import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import {
  StatusDateLine,
  StatusSummaryBox,
  StatusSummaryBoxProps,
} from "@/components/Portal/DeviceManagement/components/StatusSummaryBox";
import { SimCardDetail } from "@/services/model";
import {
  hasWatchSimLocked,
  hasWatchSimPending,
  isWatchSimPausing,
} from "@/utils/deviceUtils";
import { getElapsedPercentage, getNextBillDate } from "@/utils/tariffUtils";

type WatchSimState = "locked" | "activating" | "pausing" | "renewing";

// The watch statuses behind these never overlap, so the order only documents intent.
const getWatchSimState = (simDetail: SimCardDetail): WatchSimState => {
  // Mirrors SubscriptionStatus's own lock handling - a locked Watch SIM must
  // not present itself as actively renewing.
  if (hasWatchSimLocked(simDetail)) {
    return "locked";
  }

  // Only PENDING is still being provisioned. ORDERED is the status the
  // backend returns right after checkout — the option is already booked and
  // billed, so it must present as active (pairing help lives in WatchSimHint).
  if (hasWatchSimPending(simDetail)) {
    return "activating";
  }

  return isWatchSimPausing(simDetail) ? "pausing" : "renewing";
};

/**
 * "Watch SIM status": the same shape as `SubscriptionStatus` ("Subscription status"),
 * scoped to the Watch SIM option - whether it is still being prepared,
 * running, or scheduled to stop renewing. Its own dynamic hint lives beside
 * the tab, not inside this card - see `ShowWatchSimOptions`'s `WatchSimHint`.
 */
export const WatchSimStatus = ({
  simDetail,
  action,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
}) => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.status",
  });
  const { t: tDefault } = useTranslation();
  const { t: tDisplay } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.display",
  });

  // Same billing cycle as the subscription, so it counts down from the same fields.
  const nextBillDate = getNextBillDate(simDetail, i18n.language);
  const elapsedPercentage = getElapsedPercentage(simDetail);

  const summary: Record<WatchSimState, StatusSummaryBoxProps> = {
    locked: { state: "locked", heading: t("locked") },
    activating: {
      state: "renewing",
      heading: t("activating"),
      subline: tDisplay("pending"),
    },
    pausing: {
      state: "pausing",
      // Unlike the subscription status, a scheduled pause replaces "Active"
      // as the heading — "Active" + "Being paused on …" read as if the pause
      // had not been requested yet.
      heading: tDefault("portal:layout.tiles.statusChip.pausing"),
      subline: nextBillDate && (
        <StatusDateLine
          i18nKey="cockpit.managementTile.watchSim.status.pausing"
          date={nextBillDate}
        />
      ),
      progress: elapsedPercentage,
    },
    renewing: {
      state: "renewing",
      heading: tDefault("portal:common.status.active"),
      subline: nextBillDate && (
        <StatusDateLine i18nKey="common.nextBill" date={nextBillDate} />
      ),
      progress: elapsedPercentage,
    },
  };

  return (
    <CardModule
      dataTestid="watch-sim-status"
      title={t("title")}
      actions={action}
      bodyGap={2}
    >
      <StatusSummaryBox {...summary[getWatchSimState(simDetail)]} />
    </CardModule>
  );
};
